import { useMemo } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { LayoutGrid } from "lucide-react";
import ViewProject from "../../components/Projects/ViewProject";
import { getProjectById } from "../../api/Projects";
import type { Project } from "../../utils/interface/project";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../utils/queryKeys";

const NotFoundBlock = () => (
  <div className="min-h-full bg-[#0a0a0f] relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <svg
        className="absolute bottom-0 left-0 w-full opacity-20"
        viewBox="0 0 1200 200"
        preserveAspectRatio="none"
      >
        <path
          fill="none"
          stroke="#E761A0"
          strokeWidth="1"
          opacity="0.4"
          d="M0,100 Q300,50 600,100 T1200,100 L1200,200 L0,200 Z"
        />
        <path
          fill="none"
          stroke="#E761A0"
          strokeWidth="1"
          opacity="0.25"
          d="M0,140 Q400,80 800,140 T1600,140"
        />
      </svg>
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#E761A0]/5 to-transparent" />
    </div>
    <div className="relative z-10 p-4 sm:p-6 md:p-8">
      <Link
        to="/project-management"
        className="inline-flex items-center gap-2 text-white hover:text-[#E761A0] transition-colors duration-200 mb-6 sm:mb-8 font-medium text-sm sm:text-lg"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Projects
      </Link>
      <div className="rounded-xl sm:rounded-2xl border border-cyan-400/30 bg-black/80 shadow-xl overflow-hidden p-6 md:p-8">
        <p className="text-white/80 text-base sm:text-lg">Project not found. Please open from the project list.</p>
      </div>
    </div>
  </div>
);

const ViewProjectPage = () => {
  const { id } = useParams({ from: "/_protected/project-management/view/$id" });
  const {
    data: project,
    isLoading,
    isError,
  } = useQuery<Project>({
    queryKey: id ? queryKeys.projects.detail(id) : ["projects", "detail", "missing-id"],
    queryFn: async () => getProjectById(id as string),
    enabled: Boolean(id),
  });

  const loading = useMemo(() => Boolean(id) && isLoading, [id, isLoading]);

  if (loading) {
    return (
      <div className=" p-8 flex flex-col items-center justify-center min-h-[75vh]">
        <div className="relative w-20 h-20 shrink-0">
          <div className="absolute inset-0 w-20 h-20 border-[3px] border-[#E761A0]/20 border-t-[#E761A0] rounded-full animate-spin" />
          <LayoutGrid className="w-8 h-8 text-[#E761A0] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 shrink-0" />
        </div>
        <p className="text-white/90 text-base mt-5 font-medium">
          Loading project...
        </p>
        <p className="text-gray-500 text-sm mt-1">
          Fetching project details
        </p>
      </div>
    );
  }

  if (!id || isError || !project) {
    return <NotFoundBlock />;
  }

  return <ViewProject project={project} />;
};

export default ViewProjectPage;
